import React from 'react';
import { content } from '../data/content';
import { X, Check, AlertTriangle, Zap } from 'lucide-react';

const Comparison: React.FC = () => {
  const { comparison } = content;

  return (
    <section className="min-h-screen flex items-center justify-center py-12 bg-white relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-20 w-[500px] h-[500px] bg-red-100/40 rounded-full blur-[100px] mix-blend-multiply"></div>
        <div className="absolute bottom-1/4 -right-20 w-[500px] h-[500px] bg-blue-100/40 rounded-full blur-[100px] mix-blend-multiply"></div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-tr from-gray-50/50 via-purple-50/30 to-blue-50/50 rounded-full blur-[120px]"></div>
      </div>

      <div className="container mx-auto px-6 relative z-10 h-full flex flex-col justify-center">
        {/* Title removed as requested */}
        
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-8 md:gap-16 items-stretch w-full">
          {/* Left Column: Self-built (Pain) - Frosted Glass */}
          <div className="group relative rounded-3xl bg-white/60 backdrop-blur-xl border border-white/50 shadow-[0_8px_30px_rgba(0,0,0,0.04)] overflow-hidden hover:shadow-[0_20px_40px_rgba(0,0,0,0.08)] transition-all duration-500 ring-1 ring-gray-100/50 flex flex-col">
            <div className="absolute inset-0 bg-gradient-to-br from-white/40 to-gray-50/40 pointer-events-none"></div>
            
            <div className="p-12 text-center border-b border-gray-100/50 relative">
              <div className="w-20 h-20 mx-auto bg-red-50/80 rounded-2xl flex items-center justify-center mb-6 border border-red-100 group-hover:scale-110 transition-transform duration-500 shadow-sm">
                <AlertTriangle className="w-10 h-10 text-red-500" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">{comparison.self.title}</h3>
              <div className="h-1.5 w-24 bg-gray-200 mx-auto mt-6 rounded-full"></div>
            </div>
            
            <div className="p-10 space-y-8 relative flex-grow flex flex-col justify-center">
              {comparison.self.items.map((item, index) => (
                <div key={index} className="flex items-start group/item">
                  <div className="mt-1.5 mr-5 p-1.5 bg-red-50 rounded-full text-red-500 flex-shrink-0 border border-red-100 group-hover/item:bg-red-100 transition-colors">
                    <X size={16} />
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-gray-800 text-xl mb-2 group-hover/item:text-red-600 transition-colors">{item.title}</p>
                    <p className="text-gray-500 text-base leading-relaxed">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right Column: Us (Gain) - Frosted Glass (White) */}
          <div className="group relative rounded-3xl bg-white/60 backdrop-blur-xl border border-white/50 shadow-[0_8px_30px_rgba(59,130,246,0.1)] overflow-hidden hover:shadow-[0_20px_40px_rgba(59,130,246,0.15)] transition-all duration-500 transform hover:-translate-y-2 ring-1 ring-blue-100/50 flex flex-col">
            {/* Subtle Gradient Overlay for Distinction */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-50/30 to-indigo-50/30 pointer-events-none"></div>
            
            {/* Glowing Border Effect */}
            <div className="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none">
              <div className="absolute top-0 left-0 w-full h-[1px] bg-gradient-to-r from-transparent via-blue-400/50 to-transparent animate-shine"></div>
            </div>
            
            <div className="p-12 text-center border-b border-blue-100/50 relative">
              <div className="w-20 h-20 mx-auto bg-blue-50/80 rounded-2xl flex items-center justify-center mb-6 border border-blue-100 group-hover:scale-110 transition-transform duration-500 shadow-sm">
                <Zap className="w-10 h-10 text-blue-500" />
              </div>
              <h3 className="text-3xl font-bold text-gray-900 mb-2">{comparison.us.title}</h3>
              <div className="h-1.5 w-24 bg-gradient-to-r from-blue-400 to-indigo-400 mx-auto mt-6 rounded-full shadow-sm"></div>
            </div>
            
            <div className="p-10 space-y-8 relative flex-grow flex flex-col justify-center">
              {comparison.us.items.map((item, index) => (
                <div key={index} className="flex items-start group/item">
                  <div className="mt-1.5 mr-5 p-1.5 bg-green-50 rounded-full text-green-500 flex-shrink-0 border border-green-100 group-hover/item:bg-green-100 transition-colors shadow-sm">
                    <Check size={16} />
                  </div>
                  <div className="text-left">
                    <p className="font-bold text-gray-900 text-xl mb-2 group-hover/item:text-blue-600 transition-colors">{item.title}</p>
                    <p className="text-gray-600 text-base leading-relaxed">{item.text}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Comparison;
